import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, ChevronDown, Gem, Loader2, Minus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { apiFetch, ApiError } from '../lib/api';
import { readBillingSnapshot, writeBillingSnapshot } from '../lib/billingSnapshot';
import { queryKeys } from '../lib/queryKeys';
import { useBackNavigation } from '../hooks/useBackNavigation';

type CheckoutResponse =
  | { action: 'checkout'; url: string }
  | { action: 'upgraded'; subscription_tier: string }
  | { action: 'already_on_plan'; subscription_tier: string };

// ============================================
// Types & Constants
// ============================================

interface Plan {
  name: string;
  tier: 'free' | 'social' | 'pro';
  monthlyPrice: string;
  annualPrice: string;
  annualMonthly: string;
  period: string;
  description: string;
  upgradeText: string;
  highlighted?: boolean;
  badge?: string;
  accent?: 'teal' | 'purple';
}

const TIER_ORDER = { free: 0, social: 1, pro: 2 } as const;

const PLANS: Plan[] = [
  {
    name: 'Free',
    tier: 'free',
    monthlyPrice: '$0',
    annualPrice: '$0',
    annualMonthly: '$0',
    period: 'forever',
    description: 'Discover and track what you watch',
    upgradeText: 'Get Started',
  },
  {
    name: 'Social',
    tier: 'social',
    monthlyPrice: '$2',
    annualPrice: '$19.99',
    annualMonthly: '$1.67',
    period: '/month',
    description: 'Connect with friends and share your taste',
    upgradeText: 'Get Social',
    highlighted: true,
    badge: 'POPULAR',
    accent: 'teal',
  },
  {
    name: 'Pro',
    tier: 'pro',
    monthlyPrice: '$9.99',
    annualPrice: '$95.99',
    annualMonthly: '$8.00',
    period: '/month',
    description: 'Unlock the full Filmply AI experience',
    upgradeText: 'Upgrade to Pro',
    accent: 'purple',
  },
];

// ============================================
// Comparison Table Data
// ============================================

// Cell values: true = ✓, false = —, string = text
type CellValue = boolean | string;

interface FeatureRow {
  feature: string;
  free: CellValue;
  social: CellValue;
  pro: CellValue;
}

interface FeatureCategory {
  category: string;
  rows: FeatureRow[];
}

const COMPARISON_TABLE: FeatureCategory[] = [
  {
    category: 'Discovery',
    rows: [
      { feature: 'Browse & search movies, TV, people', free: true, social: true, pro: true },
      { feature: 'Filters (genre, year, rating, runtime, language, actor, director)', free: true, social: true, pro: true },
      { feature: 'Sort (popularity, rating, release date)', free: true, social: true, pro: true },
      { feature: 'Movie, TV & person detail pages', free: true, social: true, pro: true },
      { feature: 'Trailers', free: true, social: true, pro: true },
      { feature: '"I Feel" mood discovery', free: true, social: true, pro: true },
    ],
  },
  {
    category: 'Lists & Ratings',
    rows: [
      { feature: 'Default lists (Watchlist, Watched, Favorites)', free: true, social: true, pro: true },
      { feature: 'Rate movies & TV (1-10)', free: true, social: true, pro: true },
      { feature: 'Create custom lists', free: false, social: true, pro: true },
      { feature: 'List collaborators', free: false, social: true, pro: true },
      { feature: "Follow other people's lists", free: false, social: true, pro: true },
    ],
  },
  {
    category: 'Social',
    rows: [
      { feature: 'Follow people', free: false, social: true, pro: true },
      { feature: 'Activity feed', free: false, social: true, pro: true },
      { feature: 'Send recommendations', free: false, social: true, pro: true },
      { feature: 'Receive recommendations', free: true, social: true, pro: true },
      { feature: 'Share (copy link, social)', free: true, social: true, pro: true },
      { feature: 'Social shelves on For You', free: false, social: true, pro: true },
    ],
  },
  {
    category: 'Filmply AI & For You',
    rows: [
      { feature: 'Filmply AI chats per month', free: '10', social: '10', pro: 'Unlimited' },
      { feature: 'Filmply AI remembers your taste', free: false, social: false, pro: true },
      { feature: '"Why you\'ll love this"', free: false, social: false, pro: true },
      { feature: 'For You personalized page', free: true, social: true, pro: true },
      { feature: 'For You Controls (Hide, Tune)', free: 'Preview', social: 'Preview', pro: 'Full' },
      { feature: 'Filmply AI-powered For You hero', free: false, social: false, pro: true },
      { feature: 'Pro-exclusive For You shelves', free: false, social: false, pro: true },
    ],
  },
];

// ============================================
// FAQ
// ============================================

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What happens if I hit my chat limit?',
    answer: 'Free and Social plans include 10 Filmply chats per month. Once you reach the limit, you\'ll need to wait until the next month or upgrade to Pro for unlimited access. Your watchlists, ratings, and discovery remain fully available.',
  },
  {
    question: 'What\'s the difference between Social and Pro?',
    answer: 'Social unlocks the community side of Filmple — follow people, activity feed, custom lists, social shelves on For You, sending recommendations, and following other people\'s lists. Pro adds unlimited Filmply chats, taste memory, personalized For You controls (Hide, Why, Tune), and other AI-powered features.',
  },
  {
    question: 'Can I switch between plans?',
    answer: 'Yes! You can upgrade or downgrade at any time. To upgrade, select the plan you want and complete checkout. To downgrade, select the lower plan — you\'ll be taken to Billing to confirm. Changes take effect at the end of your current billing period.',
  },
  {
    question: 'Is there an annual option?',
    answer: 'Filmple Social is available at $2/month or $19.99/year (save over 15%). Filmple Pro is $9.99/month or $95.99/year (save 20%).',
  },
];

function FAQAccordion({ item }: { item: FAQItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/[0.06]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="text-white font-medium">{item.question}</span>
        <ChevronDown
          size={20}
          className={`text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-4">
          <p className="text-slate-400 leading-relaxed">{item.answer}</p>
        </div>
      )}
    </div>
  );
}

// ============================================
// Comparison Table Cell
// ============================================

function ComparisonCell({ value, tier }: { value: CellValue; tier: 'free' | 'social' | 'pro' }) {
  if (value === true) {
    const color = tier === 'pro' ? 'text-purple-400' : tier === 'social' ? 'text-teal-400' : 'text-green-400';
    return <Check size={18} className={color} />;
  }
  if (value === false) {
    return <Minus size={16} className="text-slate-600" />;
  }
  // String value
  const color = tier === 'pro' ? 'text-purple-400' : tier === 'social' ? 'text-teal-400' : 'text-slate-300';
  return <span className={`text-sm font-medium ${color}`}>{value}</span>;
}

// ============================================
// Main Component
// ============================================

export function PricingPage() {
  const { user, session, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  // Initialize from billing snapshot for instant display
  const [liveSub, setLiveSub] = useState<{ subscription_tier: string; billing_source: string | null } | null>(() => {
    const snap = readBillingSnapshot(user?.id);
    return snap ? { subscription_tier: snap.subscription_tier, billing_source: snap.billing_source } : null;
  });
  const [liveSubResolved, setLiveSubResolved] = useState(() => !!readBillingSnapshot(user?.id));

  // Hydrate when user becomes available (auth race)
  useEffect(() => {
    if (!user?.id || liveSub) return;
    const snap = readBillingSnapshot(user.id);
    if (snap) {
      setLiveSub({ subscription_tier: snap.subscription_tier, billing_source: snap.billing_source });
      setLiveSubResolved(true);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps — intentional: run once when user arrives, not when liveSub changes

  // Fetch live subscription data on mount to avoid stale profile cache
  useEffect(() => {
    if (!session?.access_token) return;
    const controller = new AbortController();

    (async () => {
      try {
        const data = await apiFetch<{ subscription_tier: string; billing_source: string | null }>('/subscription', {
          token: session.access_token,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setLiveSub(data);
          setLiveSubResolved(true);
          writeBillingSnapshot(user?.id, data);
        }
      } catch {
        // Fall back to profile cache when no snapshot exists, so buttons enable
        if (!controller.signal.aborted) {
          if (!liveSub && profile?.subscription_tier) {
            setLiveSub({ subscription_tier: profile.subscription_tier, billing_source: profile.billing_source || null });
          }
          setLiveSubResolved(true);
        }
      }
    })();

    // Also refresh profile in background so AuthContext cache stays fresh
    refreshProfile();

    return () => controller.abort();
  }, [session?.access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTier: string | null = liveSub?.subscription_tier ?? null;
  const currentTierOrder = TIER_ORDER[(currentTier || 'free') as keyof typeof TIER_ORDER] ?? 0;

  // Disable plan buttons until live subscription resolves (prevents stale CTA actions)
  const ctaDisabled = !!session && !liveSubResolved;

  const handlePlanClick = async (plan: Plan) => {
    if (ctaDisabled) return;
    const planOrder = TIER_ORDER[plan.tier];
    if (plan.tier === currentTier) return;

    if (!session?.access_token) {
      toast.info('Sign in to subscribe');
      navigate('/');
      return;
    }

    const isDowngrade = planOrder < currentTierOrder;

    if (isDowngrade) {
      const billingSource = liveSub?.billing_source ?? null;
      if (billingSource === 'stripe') {
        setCheckoutLoading(plan.tier);
        try {
          const data = await apiFetch<{ url: string }>('/billing-portal', {
            token: session.access_token,
            method: 'POST',
          });
          window.location.href = data.url;
        } catch (err) {
          if (err instanceof ApiError && err.code === 'NO_SUBSCRIPTION') {
            toast.error('No active subscription found');
          } else {
            toast.error('Failed to open billing portal. Please try again.');
          }
          setCheckoutLoading(null);
        }
      } else if (billingSource === 'revenuecat') {
        toast.info('Manage your subscription in the App Store or Google Play.');
      } else {
        toast.info('This subscription isn\'t managed via web billing.');
      }
      return;
    }

    // Upgrade flow
    setCheckoutLoading(plan.tier);
    try {
      const data = await apiFetch<CheckoutResponse>('/checkout', {
        token: session.access_token,
        method: 'POST',
        body: { tier: plan.tier, interval: isAnnual ? 'annual' : 'monthly' },
      });

      if (data.action === 'already_on_plan') {
        toast.info("You're already on this plan.");
        setCheckoutLoading(null);
      } else if (data.action === 'upgraded') {
        // Write optimistic snapshot so Settings loads with target tier instantly
        writeBillingSnapshot(user?.id, {
          subscription_tier: plan.tier,
          billing_source: liveSub?.billing_source ?? 'stripe',
        });
        // Purge stale For You cache so new tier shelves load fresh
        const forYouUserId = user?.id ?? profile?.id;
        if (forYouUserId) queryClient.removeQueries({ queryKey: queryKeys.forYou.apiBase(forYouUserId), exact: false });
        refreshProfile();
        setCheckoutLoading(null);
        navigate(`/settings?checkout=success&expected_tier=${plan.tier}&expected_interval=${isAnnual ? 'annual' : 'monthly'}`);
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'MULTIPLE_ACTIVE_SUBSCRIPTIONS') {
          toast.error('Multiple active subscriptions detected. Please contact support@filmple.io.');
        } else if (err.code === 'INVALID_SUBSCRIPTION_STATE') {
          toast.error('Something went wrong with your subscription. Please contact support@filmple.io.');
        } else if (err.code === 'BILLING_SOURCE_CONFLICT') {
          toast.info('Your subscription is managed through the mobile app.');
        } else if (err.code === 'PRICE_NOT_CONFIGURED') {
          toast.error('This plan is not available yet.');
        } else {
          toast.error('Failed to start checkout. Please try again.');
        }
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      setCheckoutLoading(null);
    }
  };

  const getButtonText = (plan: Plan): string => {
    if (plan.tier === currentTier) return 'Current Plan';
    const planOrder = TIER_ORDER[plan.tier];
    if (planOrder < currentTierOrder) return 'Downgrade';
    return plan.upgradeText;
  };

  return (
    <div className="min-h-screen bg-[#111111] py-12">
      <div className="max-w-6xl mx-auto px-6">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Choose Your Plan
          </h1>
          <p className="text-slate-400 text-lg">
            Unlock the full power of Filmply recommendations
          </p>
        </div>

        {/* Monthly / Annual Toggle */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-white' : 'text-slate-400'}`}>
            Monthly
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              isAnnual ? 'bg-teal-500' : 'bg-slate-600'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                isAnnual ? 'translate-x-6' : ''
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${isAnnual ? 'text-white' : 'text-slate-400'}`}>
            Annual
          </span>
          {isAnnual && (
            <span className="text-xs text-teal-400 font-medium ml-1">Save 15-20%</span>
          )}
        </div>

        {/* Contextual downgrade banner for paid users — hide when tier unknown */}
        {session && currentTier && (currentTier === 'social' || currentTier === 'pro') && (
          <p className="text-sm text-slate-400 bg-slate-800/50 rounded-lg px-4 py-3 max-w-xl mx-auto mb-8 text-center">
            To change or cancel your plan, select a tier below. Downgrades are processed through Billing and take effect at the end of your current period.
          </p>
        )}

        {/* ============================================ */}
        {/* Plan Summary Cards                          */}
        {/* ============================================ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PLANS.map((plan) => {
            const isCurrentPlan = plan.tier === currentTier;
            const isTeal = plan.accent === 'teal';
            const isPurple = plan.accent === 'purple';
            const isLoading = checkoutLoading === plan.tier;
            const displayPrice = plan.tier === 'free'
              ? '$0'
              : isAnnual ? plan.annualPrice : plan.monthlyPrice;
            const displayPeriod = plan.tier === 'free'
              ? 'forever'
              : isAnnual ? '/year' : '/month';

            return (
              <div
                key={plan.name}
                className={`relative bg-[#1A1F26]/50 rounded-2xl p-6 flex flex-col ${
                  plan.highlighted
                    ? 'border-2 border-teal-500 shadow-lg shadow-teal-500/20'
                    : isPurple
                      ? 'border border-purple-500/50'
                      : 'ring-1 ring-white/[0.08]'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-teal-500 text-white text-xs font-bold rounded-full">
                      <Gem size={12} />
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Plan Name */}
                <h2 className={`text-xl font-bold mb-2 ${
                  isTeal ? 'text-teal-400' : isPurple ? 'text-purple-400' : 'text-white'
                }`}>
                  {plan.name}
                </h2>

                {/* Price */}
                <div className="mb-3">
                  <span className="text-4xl font-bold text-white">{displayPrice}</span>
                  <span className="text-slate-400 ml-1">{displayPeriod}</span>
                  {isAnnual && plan.tier !== 'free' && (
                    <div className="text-sm text-slate-400 mt-1">
                      {plan.annualMonthly}/month
                    </div>
                  )}
                </div>

                {/* Description */}
                <p className="text-slate-400 text-sm mb-6 flex-1">{plan.description}</p>

                {/* Button */}
                <button
                  onClick={() => handlePlanClick(plan)}
                  disabled={isCurrentPlan || isLoading || ctaDisabled}
                  className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                    isCurrentPlan
                      ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                      : isTeal
                        ? 'bg-teal-500 text-white hover:bg-teal-600'
                        : isPurple
                          ? 'bg-purple-500 text-white hover:bg-purple-600'
                          : 'bg-slate-700 text-white hover:bg-slate-600'
                  } ${isLoading ? 'opacity-70 cursor-wait' : ''} ${ctaDisabled && !isCurrentPlan ? 'opacity-50 cursor-wait' : ''}`}
                >
                  {isLoading && <Loader2 size={16} className="animate-spin" />}
                  {getButtonText(plan)}
                </button>
              </div>
            );
          })}
        </div>

        {/* ============================================ */}
        {/* Feature Comparison Table                     */}
        {/* ============================================ */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Compare Plans
          </h2>

          {/* Desktop table (hidden on mobile) */}
          <div className="hidden md:block">
            <table className="w-full">
              {/* Sticky header */}
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#111111]">
                  <th className="text-left py-3 px-4 text-slate-400 text-sm font-medium w-[40%]">Feature</th>
                  <th className="text-center py-3 px-4 text-white font-semibold w-[20%]">Free</th>
                  <th className="text-center py-3 px-4 text-teal-400 font-semibold w-[20%]">Social</th>
                  <th className="text-center py-3 px-4 text-purple-400 font-semibold w-[20%]">Pro</th>
                </tr>
                <tr>
                  <td colSpan={4} className="h-px bg-white/[0.08]" />
                </tr>
              </thead>
              <tbody>
                {COMPARISON_TABLE.map((category) => (
                  <ComparisonCategory key={category.category} category={category} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile table (horizontal scroll, sticky feature column) */}
          <div className="md:hidden overflow-x-auto -mx-6">
            <div className="min-w-[520px] px-6">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#111111]">
                    <th className="sticky left-0 z-10 bg-[#111111] text-left py-3 px-3 text-slate-400 text-sm font-medium w-[44%]">Feature</th>
                    <th className="text-center py-3 px-2 text-white font-semibold text-sm w-[18%]">Free</th>
                    <th className="text-center py-3 px-2 text-teal-400 font-semibold text-sm w-[19%]">Social</th>
                    <th className="text-center py-3 px-2 text-purple-400 font-semibold text-sm w-[19%]">Pro</th>
                  </tr>
                  <tr>
                    <td colSpan={4} className="h-px bg-white/[0.08]" />
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_TABLE.map((category) => (
                    <ComparisonCategory key={category.category} category={category} mobile />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* FAQ Section                                  */}
        {/* ============================================ */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="bg-[#1A1F26]/30 rounded-xl p-6">
            {FAQ_ITEMS.map((item, index) => (
              <FAQAccordion key={index} item={item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Comparison Table Category Component
// ============================================

function ComparisonCategory({ category, mobile }: { category: FeatureCategory; mobile?: boolean }) {
  return (
    <>
      {/* Category header */}
      <tr>
        <td
          colSpan={4}
          className={`pt-6 pb-2 ${mobile ? 'px-3 sticky left-0 bg-[#111111]' : 'px-4'} border-t border-white/[0.06]`}
        >
          <span className="text-white font-semibold text-sm">{category.category}</span>
        </td>
      </tr>
      {/* Feature rows */}
      {category.rows.map((row, idx) => (
        <tr
          key={row.feature}
          className={idx % 2 === 0 ? 'bg-[#1A1F26]/30' : ''}
        >
          <td className={`py-3 ${mobile ? 'px-3 text-xs sticky left-0 z-[5]' : 'px-4 text-sm'} text-slate-300 ${idx % 2 === 0 ? (mobile ? 'bg-[#161a20]' : 'bg-[#1A1F26]/30') : (mobile ? 'bg-[#111111]' : '')}`}>
            {row.feature}
          </td>
          <td className={`py-3 ${mobile ? 'px-2' : 'px-4'} text-center`}>
            <div className="flex justify-center">
              <ComparisonCell value={row.free} tier="free" />
            </div>
          </td>
          <td className={`py-3 ${mobile ? 'px-2' : 'px-4'} text-center`}>
            <div className="flex justify-center">
              <ComparisonCell value={row.social} tier="social" />
            </div>
          </td>
          <td className={`py-3 ${mobile ? 'px-2' : 'px-4'} text-center`}>
            <div className="flex justify-center">
              <ComparisonCell value={row.pro} tier="pro" />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}
